import React from 'react';
import type { PlanWithAgePremiums } from '@/components/widgets/PlansSection';
import type { AllRatesModalCompareProps } from '@/components/elements/MonthlyPremium/types';
import Button from '@/components/elements/Button/Button';
import { PlanCard } from '@/components/elements/PlanCard';
import { formatCurrencyWithCommas } from '@/utils/formatNumber';
import Modal from '@/components/elements/Modal/Modal';

export function parsePremiumValue(s?: string): number {
    return parseFloat(String(s ?? '').replace(/[^0-9.]/g, '')) || 0;
}

function formatPremiumForAge(age: number, p18: number, p65: number): string {
    if (!p18 && !p65) return '—';
    const low = p18 || p65;
    const high = p65 || p18;
    if (age < 18) return formatCurrencyWithCommas(low);
    if (age >= 65) return formatCurrencyWithCommas(high);
    const t = (age - 18) / (65 - 18);
    return formatCurrencyWithCommas(low + t * (high - low));
}

function formatPremiumForAgeLabel(label: string, p18: number, p65: number): string {
    if (label === '0–14') return formatPremiumForAge(14, p18, p65);
    if (label === '65+') return formatPremiumForAge(65, p18, p65);
    const n = parseInt(label, 10);
    return Number.isFinite(n) ? formatPremiumForAge(n, p18, p65) : '—';
}

const RATES_COL1: string[] = ['0–14', ...Array.from({ length: 16 }, (_, i) => String(15 + i))];
const RATES_COL2 = Array.from({ length: 17 }, (_, i) => String(31 + i));
const RATES_COL3 = [...Array.from({ length: 17 }, (_, i) => String(48 + i)), '65+'];

function RatesColumn({ labels, p18, p65 }: { labels: string[]; p18: number; p65: number }) {
    return (
        <div className="min-w-0 w-full overflow-hidden rounded-2xl border border-gray-200">
            <div className="grid w-full grid-cols-[minmax(0,1fr)_minmax(5.5rem,auto)] gap-x-3 border-b border-gray-200 bg-gray-100 px-3 py-3 text-xs font-semibold text-gray-900 sm:text-sm">
                <span className="text-base font-normal min-w-0">Age</span>
                <span className="text-base font-normal min-w-0 text-right">Monthly Premium</span>
            </div>
            <div className="flex w-full flex-col">
                {labels.map((lab) => (
                    <div
                        key={lab}
                        className="grid w-full grid-cols-2 gap-x-3 border-b border-gray-100 px-3 py-3 text-xs sm:text-sm last:border-b-0"
                    >
                        <span className="min-w-0 text-sm font-normal text-gray-900">{lab}</span>
                        <span className="min-w-0 text-left text-base font-normal text-brand-700">
                            {formatPremiumForAgeLabel(lab, p18, p65)}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

export type { AllRatesModalCompareProps };

export function AllRatesModal({
    isOpen,
    onClose,
    plan,
    onAddToCompare,
    zClassName = 'z-[200]',
    familyAges = [],
    employerContributionAmount = null,
    isStateCountrySearch,
    onToggleCompare,
    isCompared,
    comparePosition,
    compareTotal,
}: {
    isOpen: boolean;
    onClose: () => void;
    plan: PlanWithAgePremiums;
    onAddToCompare?: () => void;
    lockBodyScroll?: boolean;
    zClassName?: string;
    familyAges?: string[];
    employerContributionAmount?: number | null;
    /** Defaults to true when the plan has Age 18 / 65 premiums. */
    isStateCountrySearch?: boolean;
} & AllRatesModalCompareProps) {
    const p18 = parsePremiumValue(plan.premiumAge18);
    const p65 = parsePremiumValue(plan.premiumAge65);
    const stateSearch = isStateCountrySearch ?? !!(plan.premiumAge18 || plan.premiumAge65);
    /** Same action as the embedded plan card: prefer toggle (shop / compare modal), else legacy callback. */
    const compareFooterAction = onToggleCompare ?? onAddToCompare;
    const showCompareFooterButton = Boolean(compareFooterAction);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="View All Rates"
            size="5xl"
            className={zClassName}
            modalBoxClass="!max-w-5xl max-h-[90vh] overflow-y-auto"
            actions={
                <div className="flex w-full flex-wrap justify-end gap-3">
                    <Button type="button" variant="ghost" onClick={onClose}>
                        Close
                    </Button>
                    {showCompareFooterButton ? (
                        <Button
                            type="button"
                            variant="primary"
                            onClick={() => {
                                compareFooterAction?.();
                                onClose();
                            }}
                        >
                            {isCompared ? 'Remove from compare' : 'Add to Compare'}
                        </Button>
                    ) : null}
                </div>
            }
        >
            <div className="mb-6 w-full min-w-0">
                <PlanCard
                    key={plan.id}
                    plan={plan}
                    isStateCountrySearch={stateSearch}
                    familyAges={familyAges}
                    employerContributionAmount={employerContributionAmount}
                    defaultExpanded
                    fullWidth
                    hideViewAllRatesButton
                    onToggleCompare={onToggleCompare}
                    isCompared={isCompared}
                    comparePosition={comparePosition}
                    compareTotal={compareTotal}
                />
            </div>

            <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-3 md:gap-4">
                <RatesColumn labels={RATES_COL1} p18={p18} p65={p65} />
                <RatesColumn labels={RATES_COL2} p18={p18} p65={p65} />
                <RatesColumn labels={RATES_COL3} p18={p18} p65={p65} />
            </div>
        </Modal>
    );
}
